import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Users, ShieldCheck, FileText, Shield, Trash2, UserCog } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => Promise.all([http.get("/admin/users"), http.get("/admin/stats")])
    .then(([u, s]) => { setUsers(u.data); setStats(s.data); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const setRole = async (id, role) => { try { await http.put(`/admin/users/${id}/role`, { role }); toast.success(`Role set to ${role}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { try { await http.delete(`/admin/users/${id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!users || !stats) return <Loading />;

  const cards = [
    { label: "Total Users", value: stats.total_users, icon: Users, cls: "bg-slate-100 text-[#0B132B]" },
    { label: "Verified", value: stats.verified_users, icon: ShieldCheck, cls: "bg-emerald-50 text-emerald-600" },
    { label: "Invoices", value: stats.total_invoices, icon: FileText, cls: "bg-blue-50 text-blue-600" },
    { label: "Admins", value: stats.admins, icon: Shield, cls: "bg-amber-50 text-amber-600" },
  ];

  return (
    <div>
      <PageHeader title="Admin Panel" subtitle="Manage all users across the platform." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-5" data-testid={`admin-stat-${c.label.replace(/\s/g,"-").toLowerCase()}`}>
            <div className={`w-10 h-10 rounded-lg ${c.cls} flex items-center justify-center`}><c.icon className="w-5 h-5" /></div>
            <div className="mt-4 font-display text-2xl font-bold text-[#0B132B]">{c.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="p-5 border-b border-slate-100"><h3 className="font-display font-semibold text-[#0B132B]">All Users</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">User</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Joined</th>
                <th className="px-5 py-3 font-semibold text-center">Invoices</th>
                <th className="px-5 py-3 font-semibold text-center">Role</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`admin-user-${u.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-semibold text-[#0B132B] flex items-center gap-2">{u.name}{u.is_demo && <span className="text-[10px] bg-[#D4AF37]/15 text-[#B89628] px-1.5 py-0.5 rounded font-bold">DEMO</span>}</div>
                    <div className="text-xs text-slate-400">{u.email}{u.email_verified ? " · verified" : " · unverified"}</div>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3.5 text-center text-slate-600">{u.invoice_count}</td>
                  <td className="px-5 py-3.5 text-center"><StatusBadge status={u.role === "admin" ? "accepted" : "sent"} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      {u.id !== user.id && !u.is_demo && (
                        <Button variant="ghost" size="sm" onClick={() => setRole(u.id, u.role === "admin" ? "user" : "admin")} data-testid={`toggle-role-${u.id}`}>
                          <UserCog className="w-4 h-4 mr-1" />{u.role === "admin" ? "Demote" : "Make Admin"}
                        </Button>
                      )}
                      {u.role !== "admin" && !u.is_demo && (
                        <ConfirmDelete onConfirm={() => remove(u.id)} title="Delete user & all their data?" description="This permanently removes the user and every invoice, quote, customer and record they own." trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
